import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";

/** The four header destinations, shared by the desktop nav and the sheet. */
export const NAV_LINKS = [
  { href: "/#services", key: "header.navServices" },
  { href: "/#team", key: "header.navTeam" },
  { href: "/#faq", key: "header.navFaq" },
  { href: "/careers", key: "header.navCareers" },
] as const;

export const isCurrent = (href: string): boolean =>
  !href.includes("#") &&
  typeof window !== "undefined" &&
  window.location.pathname.startsWith(href);

const FOCUSABLE =
  'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const DESKTOP = "(min-width: 1024px)";
const DURATION_MS = 200; // --dur-2

/** Lock or release the page behind the sheet: scroll lock plus `inert` on main and footer. */
const setPageLocked = (locked: boolean) => {
  const html = document.documentElement;
  html.style.overflow = locked ? "hidden" : "";
  document.querySelectorAll<HTMLElement>(".site > main, .site > .site-footer").forEach((el) => {
    if (locked) el.setAttribute("inert", "");
    else el.removeAttribute("inert");
  });
};

type Props = {
  id: string;
  open: boolean;
  /** Close the sheet; `restoreFocus` returns focus to the toggle. */
  onClose: (restoreFocus: boolean) => void;
  /** Element whose focusables form the Tab cycle (the header, which holds the sheet). */
  trapRoot: React.RefObject<HTMLElement>;
};

/**
 * Full-height mobile sheet (spec c.10): fades and slides 8 px in 200 ms, traps Tab
 * within the header + sheet, Esc closes and refocuses the toggle, a link click
 * closes, the page behind is inert and scroll-locked, and it closes at >= 1024 px.
 */
const MobileMenu: React.FC<Props> = ({ id, open, onClose, trapRoot }) => {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);

  // Mount, then animate in on the next frame; animate out, then unmount.
  useEffect(() => {
    if (open) {
      setMounted(true);
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setShown(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    setShown(false);
    const timer = window.setTimeout(() => setMounted(false), DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  // Focus the first link once the sheet is in the DOM.
  useEffect(() => {
    if (open && mounted) firstLinkRef.current?.focus();
  }, [open, mounted]);

  // Page lock while open.
  useEffect(() => {
    if (!open) return;
    setPageLocked(true);
    return () => setPageLocked(false);
  }, [open]);

  // Esc, Tab trap, and close when the viewport reaches the desktop layout.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose(true);
        return;
      }
      if (e.key !== "Tab" || !trapRoot.current) return;
      const items = Array.from(trapRoot.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== "hidden"
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside = !!active && trapRoot.current.contains(active);
      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    const mq = window.matchMedia(DESKTOP);
    const onMq = () => {
      if (mq.matches) onClose(false);
    };
    document.addEventListener("keydown", onKey);
    mq.addEventListener?.("change", onMq);
    return () => {
      document.removeEventListener("keydown", onKey);
      mq.removeEventListener?.("change", onMq);
    };
  }, [open, onClose, trapRoot]);

  // Release the page, close, then scroll to same-page anchors ourselves: the
  // native jump can be lost while the locked page is being released.
  const onLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      setPageLocked(false);
      onClose(false);
      const url = new URL(e.currentTarget.href, window.location.href);
      if (url.pathname !== window.location.pathname || !url.hash) return;
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!target) return;
      e.preventDefault();
      window.history.pushState(null, "", url.hash);
      const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "start" });
        if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
        target.focus({ preventScroll: true });
      });
    },
    [onClose]
  );

  return (
    <div id={id} className="sheet" hidden={!mounted} data-open={shown ? "true" : "false"}>
      <nav aria-label={t("header.navAriaLabel")}>
        <ul className="sheet-list">
          {NAV_LINKS.map((l, i) => (
            <li key={l.href}>
              <a
                ref={i === 0 ? firstLinkRef : undefined}
                className="sheet-link"
                href={l.href}
                aria-current={isCurrent(l.href) ? "page" : undefined}
                onClick={onLinkClick}
              >
                {t(l.key)}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      <LanguageSelector block className="sheet-lang" />
      <a className="btn btn--primary btn--lg btn--block sheet-cta" href="/#contact" onClick={onLinkClick}>
        {t("header.cta")}
      </a>
    </div>
  );
};

export default MobileMenu;
